import { z } from "zod";

/**
 * Shared select option lists. `value` matches the backend enums in
 * `validators/employee.ts`; `label` is the human-facing text.
 */
export const TITLE_OPTIONS = [
  { value: "mr", label: "Mr" },
  { value: "mrs", label: "Mrs" },
  { value: "ms", label: "Ms" },
  { value: "miss", label: "Miss" },
  { value: "dr", label: "Dr" },
  { value: "prof", label: "Prof" },
] as const;

export const EMPLOYMENT_TYPE_OPTIONS = [
  { value: "full-time", label: "Full-time" },
  { value: "part-time", label: "Part-time" },
  { value: "contract", label: "Contract" },
  { value: "temporary", label: "Temporary" },
  { value: "intern", label: "Intern" },
  { value: "consultant", label: "Consultant" },
] as const;

export const EMPLOYMENT_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "probation", label: "Probation" },
  { value: "on-leave", label: "On leave" },
  { value: "suspended", label: "Suspended" },
  { value: "terminated", label: "Terminated" },
  { value: "resigned", label: "Resigned" },
] as const;

export const GENDER_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
  { value: "prefer-not-to-say", label: "Prefer not to say" },
] as const;

const MARITAL_STATUS_OPTIONS = [
  { value: "single", label: "Single" },
  { value: "married", label: "Married" },
  { value: "divorced", label: "Divorced" },
  { value: "widowed", label: "Widowed" },
  { value: "separated", label: "Separated" },
  { value: "other", label: "Other" },
] as const;

export const WORK_ARRANGEMENT_OPTIONS = [
  { value: "onsite", label: "On-site" },
  { value: "hybrid", label: "Hybrid" },
  { value: "remote", label: "Fully remote" },
] as const;

const ACCOUNT_TYPE_OPTIONS = [
  { value: "current", label: "Current / Checking" },
  { value: "savings", label: "Savings" },
  { value: "other", label: "Other" },
] as const;

export const PAY_FREQUENCY_OPTIONS = [
  { value: "monthly", label: "Monthly" },
  { value: "biweekly", label: "Bi-weekly" },
  { value: "weekly", label: "Weekly" },
  { value: "daily", label: "Daily" },
] as const;

const YES_NO_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
] as const;

type Option = { value: string; label: string };
type FieldKind =
  | "text"
  | "email"
  | "tel"
  | "date"
  | "textarea"
  | "select"
  | "country"
  | "nationality"
  // The supervisor: a linked colleague, or a typed name.
  | "manager";

type FieldDef = {
  name: string;
  label: string;
  kind?: FieldKind;
  required?: boolean;
  options?: readonly Option[];
  max?: number;
  default?: string;
  full?: boolean; // span both grid columns
};

type SectionDef = {
  id: string;
  title: string;
  badge: "required" | "optional";
  description?: string;
  fields: readonly FieldDef[];
};

/**
 * The single source of truth for the employee form: every section and field.
 * It drives the field-name union, the zod schema, the default values, and the
 * rendered form, so adding a field here wires it everywhere (the backend column
 * + validator must be updated to match).
 */
export const EMPLOYEE_SECTIONS = [
  {
    id: "core",
    title: "Core Profile",
    badge: "required",
    description:
      "Only this section is required to create a complete employee profile. Everything else can be filled later.",
    fields: [
      { name: "title", label: "Title", kind: "select", options: TITLE_OPTIONS },
      { name: "firstName", label: "First Name", required: true },
      { name: "middleName", label: "Middle Name" },
      { name: "lastName", label: "Last Name / Surname", required: true },
      { name: "preferredName", label: "Preferred Name / Nickname" },
      {
        name: "personalEmail",
        label: "Personal Email Address",
        kind: "email",
        required: true,
      },
      {
        name: "personalMobile",
        label: "Personal Mobile Number",
        kind: "tel",
        required: true,
      },
      { name: "workEmail", label: "Work Email Address", kind: "email" },
      {
        name: "hireDate",
        label: "Date of Employment / Hire Date",
        kind: "date",
        required: true,
      },
      { name: "startDate", label: "Employment Start Date", kind: "date" },
      {
        name: "employmentType",
        label: "Employment Type",
        kind: "select",
        required: true,
        options: EMPLOYMENT_TYPE_OPTIONS,
      },
      { name: "department", label: "Department", required: true },
      { name: "jobTitle", label: "Job Title / Position", required: true },
      { name: "supervisor", label: "Direct Supervisor / Line Manager Name", kind: "manager" },
      { name: "employeeNumber", label: "Employee ID / Staff Number" },
      {
        name: "employmentStatus",
        label: "Employment Status",
        kind: "select",
        required: true,
        options: EMPLOYMENT_STATUS_OPTIONS,
        default: "active",
      },
    ],
  },
  {
    id: "personal",
    title: "Section 1: Personal Information",
    badge: "optional",
    fields: [
      { name: "dateOfBirth", label: "Date of Birth", kind: "date" },
      {
        name: "gender",
        label: "Gender",
        kind: "select",
        options: GENDER_OPTIONS,
      },
      {
        name: "maritalStatus",
        label: "Marital Status",
        kind: "select",
        options: MARITAL_STATUS_OPTIONS,
      },
      { name: "nationality", label: "Nationality", kind: "nationality" },
      { name: "countryOfBirth", label: "Country of Birth", kind: "country" },
      { name: "nationalId", label: "National ID / Identity Number" },
      { name: "passportNumber", label: "Passport Number" },
      { name: "passportExpiry", label: "Passport Expiry Date", kind: "date" },
      { name: "socialSecurityNumber", label: "Social Security Number" },
      { name: "taxIdentificationNumber", label: "Tax Identification Number" },
      { name: "raceEthnicity", label: "Race / Ethnicity" },
      { name: "religion", label: "Religion" },
      { name: "homeLanguage", label: "Home / First Language" },
      { name: "otherLanguages", label: "Other Languages Spoken" },
      { name: "profilePhotoUrl", label: "Profile Photo URL", full: true },
    ],
  },
  {
    id: "contact",
    title: "Section 2: Contact Information",
    badge: "optional",
    fields: [
      { name: "homeTelephone", label: "Home Telephone Number", kind: "tel" },
      {
        name: "workPhoneExtension",
        label: "Work Phone / Extension",
        kind: "tel",
      },
      {
        name: "residentialAddress1",
        label: "Residential Address Line 1",
        full: true,
      },
      {
        name: "residentialAddress2",
        label: "Residential Address Line 2",
        full: true,
      },
      { name: "residentialCity", label: "Residential City / Town" },
      {
        name: "residentialState",
        label: "Residential State / Province / Region",
      },
      { name: "residentialPostalCode", label: "Residential Postal / ZIP Code" },
      {
        name: "residentialCountry",
        label: "Residential Country",
        kind: "country",
      },
      {
        name: "mailingSameAsResidential",
        label: "Mailing Address Same as Residential?",
        kind: "select",
        options: YES_NO_OPTIONS,
      },
      { name: "mailingAddress1", label: "Mailing Address Line 1", full: true },
      { name: "mailingAddress2", label: "Mailing Address Line 2", full: true },
      { name: "mailingCity", label: "Mailing City / Town" },
      { name: "mailingState", label: "Mailing State / Province / Region" },
      { name: "mailingPostalCode", label: "Mailing Postal / ZIP Code" },
      { name: "mailingCountry", label: "Mailing Country", kind: "country" },
    ],
  },
  {
    id: "emergency",
    title: "Section 3: Emergency Contacts",
    badge: "optional",
    fields: [
      { name: "emergencyContact1Name", label: "Contact 1 — Name" },
      {
        name: "emergencyContact1Relationship",
        label: "Contact 1 — Relationship",
      },
      {
        name: "emergencyContact1PrimaryPhone",
        label: "Contact 1 — Primary Phone",
        kind: "tel",
      },
      {
        name: "emergencyContact1SecondaryPhone",
        label: "Contact 1 — Secondary Phone",
        kind: "tel",
      },
      {
        name: "emergencyContact1Email",
        label: "Contact 1 — Email",
        kind: "email",
      },
      {
        name: "emergencyContact1Address",
        label: "Contact 1 — Address",
        full: true,
      },
      { name: "emergencyContact2Name", label: "Contact 2 — Name" },
      {
        name: "emergencyContact2Relationship",
        label: "Contact 2 — Relationship",
      },
      {
        name: "emergencyContact2PrimaryPhone",
        label: "Contact 2 — Primary Phone",
        kind: "tel",
      },
      {
        name: "emergencyContact2SecondaryPhone",
        label: "Contact 2 — Secondary Phone",
        kind: "tel",
      },
      {
        name: "emergencyContact2Email",
        label: "Contact 2 — Email",
        kind: "email",
      },
      { name: "emergencyContact3Name", label: "Contact 3 — Name" },
      {
        name: "emergencyContact3Relationship",
        label: "Contact 3 — Relationship",
      },
      {
        name: "emergencyContact3PrimaryPhone",
        label: "Contact 3 — Primary Phone",
        kind: "tel",
      },
    ],
  },
  {
    id: "employment",
    title: "Section 4: Employment Information",
    badge: "optional",
    fields: [
      { name: "probationPeriod", label: "Probation Period" },
      { name: "jobGradeLevel", label: "Job Grade / Level" },
      { name: "salaryBandPayGrade", label: "Salary Band / Pay Grade" },
      { name: "subDepartmentTeam", label: "Sub-Department / Team" },
      { name: "businessUnit", label: "Business Unit" },
      { name: "costCentreBudgetCode", label: "Cost Centre / Budget Code" },
      { name: "branchOfficeLocation", label: "Branch / Office Location" },
      { name: "workSiteAddress", label: "Work Site Address", full: true },
      {
        name: "workArrangement",
        label: "Work Arrangement",
        kind: "select",
        options: WORK_ARRANGEMENT_OPTIONS,
      },
      { name: "hybridDaysPattern", label: "Hybrid Days / Pattern" },
      { name: "lineManagerEmployeeId", label: "Line Manager Employee ID" },
      { name: "secondaryManager", label: "Secondary / Dotted-Line Manager" },
      { name: "hrBusinessPartner", label: "HR Business Partner Assigned" },
      {
        name: "dateFirstAppointmentCurrentRole",
        label: "Date of First Appointment to Current Role",
        kind: "date",
      },
      {
        name: "previouslyEmployedHere",
        label: "Previously Employed Here?",
        kind: "select",
        options: YES_NO_OPTIONS,
      },
    ],
  },
  {
    id: "contract",
    title: "Section 5: Contract & Remuneration",
    badge: "optional",
    fields: [
      { name: "contractStartDate", label: "Contract Start Date", kind: "date" },
      { name: "contractEndDate", label: "Contract End Date", kind: "date" },
      { name: "contractReferenceNumber", label: "Contract Reference Number" },
      { name: "noticePeriod", label: "Notice Period" },
      { name: "basicBaseSalary", label: "Basic / Base Salary" },
      { name: "totalCostToCompany", label: "Total Cost to Company (CTC)" },
      { name: "currency", label: "Currency" },
      {
        name: "payFrequency",
        label: "Pay Frequency",
        kind: "select",
        options: PAY_FREQUENCY_OPTIONS,
      },
      { name: "paymentMethod", label: "Payment Method" },
      { name: "salaryReviewDate", label: "Salary Review Date", kind: "date" },
      { name: "allowanceHousing", label: "Allowance — Housing" },
      { name: "allowanceTransport", label: "Allowance — Transport" },
      { name: "allowanceMeal", label: "Allowance — Meal" },
      { name: "allowancePhone", label: "Allowance — Phone" },
      { name: "allowanceCar", label: "Allowance — Car" },
      { name: "allowanceShift", label: "Allowance — Shift" },
      { name: "allowanceOvertime", label: "Allowance — Overtime" },
      { name: "allowanceOther", label: "Allowance — Other" },
      { name: "deductionPaye", label: "Deduction — PAYE" },
      { name: "deductionPensionPercent", label: "Deduction — Pension %" },
      { name: "deductionMedicalAid", label: "Deduction — Medical Aid" },
      { name: "deductionUnionDues", label: "Deduction — Union Dues" },
      { name: "deductionLoans", label: "Deduction — Loans" },
      { name: "deductionOther", label: "Deduction — Other" },
    ],
  },
  {
    id: "banking",
    title: "Section 6: Banking & Payroll",
    badge: "optional",
    fields: [
      { name: "bankName", label: "Bank Name" },
      { name: "bankBranchName", label: "Branch Name" },
      { name: "bankBranchCode", label: "Branch / Sort / Routing Code" },
      { name: "accountHolderName", label: "Account Holder Name" },
      { name: "accountNumber", label: "Account Number" },
      {
        name: "accountType",
        label: "Account Type",
        kind: "select",
        options: ACCOUNT_TYPE_OPTIONS,
      },
      { name: "swiftBic", label: "SWIFT / BIC" },
      { name: "iban", label: "IBAN" },
      {
        name: "accountInEmployeeName",
        label: "Bank Account in Employee Name?",
        kind: "select",
        options: YES_NO_OPTIONS,
      },
      {
        name: "thirdPartyPaymentAuth",
        label: "Third-party Payment Authorization Details",
        kind: "textarea",
        full: true,
      },
      { name: "taxFilingStatus", label: "Tax Filing Status" },
      {
        name: "taxExemptionsClaimed",
        label: "Tax Exemptions / Allowances Claimed",
      },
      {
        name: "otherTaxNotes",
        label: "Other Tax Notes",
        kind: "textarea",
        full: true,
      },
    ],
  },
] as const satisfies readonly SectionDef[];

/** The literal union of every field name across all sections. */
export type EmployeeFieldName = (typeof EMPLOYEE_SECTIONS)[number]["fields"][number]["name"];

/**
 * Render-facing descriptors: every optional property is present (unlike the
 * narrow `as const` element types), so the form and schema builders can read
 * `kind`/`options`/`required` without per-member narrowing. `name` keeps the
 * literal union so form field names stay type-checked.
 */
export type EmployeeField = Omit<FieldDef, "name"> & {
  name: EmployeeFieldName;
};
export type EmployeeSection = Omit<SectionDef, "fields"> & {
  fields: readonly EmployeeField[];
};

/** All sections, typed for iteration (the const is assignable to this). */
const ALL_SECTIONS: readonly EmployeeSection[] = EMPLOYEE_SECTIONS;

/** The form value shape — every field is a string ("" when unset). */
export type EmployeeFormValues = Record<EmployeeFieldName, string> & {
  /** The supervisor as a linked employee ("" = not linked). Not a section
   *  field: it has no column in the CSV, where the supervisor is a name. */
  managerEmployeeId: string;
};

/**
 * A persisted employee returned by the API. Every editable field plus the
 * server-managed id and timestamps.
 */
export type EmployeeData = EmployeeFormValues & {
  id: string;
  createdAt: string;
  updatedAt: string;
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Builds the zod validator for one field from its descriptor. */
function fieldSchema(f: EmployeeField): z.ZodTypeAny {
  const max = f.max ?? 200;
  if (f.kind === "select") {
    const values = (f.options ?? []).map((o) => o.value) as [string, ...string[]];
    return f.required
      ? z.enum(values, { message: `Select ${f.label.toLowerCase()}` })
      : z.enum(values).or(z.literal(""));
  }
  if (f.kind === "email") {
    const base = z.string().trim().max(max).email("Enter a valid email");
    return f.required
      ? z.string().trim().min(1, `${f.label} is required`).max(max).email("Enter a valid email")
      : base.or(z.literal(""));
  }
  if (f.kind === "date") {
    return f.required
      ? z.string().trim().min(1, `${f.label} is required`).regex(ISO_DATE, "Use a valid date")
      : z.string().trim().regex(ISO_DATE, "Use a valid date").or(z.literal(""));
  }
  // text / tel / textarea
  return f.required
    ? z.string().trim().min(1, `${f.label} is required`).max(max)
    : z.string().trim().max(max);
}

function buildSchema(): z.ZodType<EmployeeFormValues, EmployeeFormValues> {
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const section of ALL_SECTIONS) {
    for (const f of section.fields) {
      shape[f.name] = fieldSchema(f);
    }
  }
  shape.managerEmployeeId = z.string();
  return z.object(shape) as unknown as z.ZodType<EmployeeFormValues, EmployeeFormValues>;
}

/** The Core Profile + Sections 1–6 validator, built from `EMPLOYEE_SECTIONS`. */
export const employeeFormSchema = buildSchema();

/** Maps a persisted employee (or `null` for a new one) to the form shape. */
export function toEmployeeFormValues(src: EmployeeData | null): EmployeeFormValues {
  const source = (src ?? {}) as Record<string, string>;
  const out: Record<string, string> = {};
  for (const section of ALL_SECTIONS) {
    for (const f of section.fields) {
      out[f.name] = source[f.name] ?? f.default ?? "";
    }
  }
  out.managerEmployeeId = source.managerEmployeeId ?? "";
  return out as EmployeeFormValues;
}

/** A human-readable label for an option `value`, falling back to the raw value. */
export function labelFor(options: readonly Option[], value: string): string {
  return options.find((o) => o.value === value)?.label ?? value;
}

/**
 * The lightweight employee shape the directory endpoint returns — just the
 * columns pickers, name lookups and profile links render. Screens that only
 * show/choose people use this instead of the full {@link EmployeeData} row, so
 * they never ship the whole 117-column record across the workspace.
 */
export type EmployeeDirectoryEntry = Pick<
  EmployeeData,
  | "id"
  | "employeeNumber"
  | "firstName"
  | "lastName"
  | "jobTitle"
  | "department"
  | "employmentType"
  | "employmentStatus"
  | "workEmail"
  | "personalEmail"
>;

/** The display name for an employee row. */
export function employeeFullName(e: EmployeeDirectoryEntry): string {
  return [e.firstName, e.lastName].filter(Boolean).join(" ").trim();
}

/** Muted secondary line that disambiguates same-named employees ("Lead · Sales"). */
export function employeeSubtitle(e: EmployeeDirectoryEntry): string {
  return [e.jobTitle, e.department].filter(Boolean).join(" · ");
}

/** Single-line label for plain-text selects: "Jane Doe — #EMP-12 — Lead · Sales". */
export function employeeOptionLabel(e: EmployeeDirectoryEntry): string {
  const parts = [employeeFullName(e)];
  if (e.employeeNumber) parts.push(`#${e.employeeNumber}`);
  const sub = employeeSubtitle(e);
  if (sub) parts.push(sub);
  return parts.join(" — ");
}

/** A flat, render-friendly descriptor of one exportable/importable field. */
export type EmployeeFlatField = {
  name: EmployeeFieldName;
  label: string;
  required: boolean;
  sectionId: string;
  sectionTitle: string;
};

/** Every field across all sections, flattened with its section + required flag. */
export const EMPLOYEE_FLAT_FIELDS: EmployeeFlatField[] = ALL_SECTIONS.flatMap((section) =>
  section.fields.map((f) => ({
    name: f.name,
    label: f.label,
    required: Boolean(f.required),
    sectionId: section.id,
    sectionTitle: section.title,
  })),
);

/** The required Core Profile field names — a row can't import without these. */
export const REQUIRED_FIELD_NAMES: EmployeeFieldName[] = EMPLOYEE_FLAT_FIELDS.filter(
  (f) => f.required,
).map((f) => f.name);

/**
 * Example values for the required fields, used as a sample row in the import
 * template. They double as format guidance — select fields take the underlying
 * *value* (e.g. `full-time`, not "Full-time") and dates are `yyyy-mm-dd`.
 * Optional fields are left blank so the example row imports cleanly as-is.
 */
const TEMPLATE_EXAMPLES: Partial<Record<EmployeeFieldName, string>> = {
  firstName: "Jane",
  lastName: "Doe",
  personalEmail: "jane.doe@example.com",
  personalMobile: "+1 555 0100",
  hireDate: "2024-01-15",
  employmentType: "full-time",
  department: "Engineering",
  jobTitle: "Software Engineer",
  employmentStatus: "active",
};

/** One example row aligned to {@link EMPLOYEE_FLAT_FIELDS} for the template. */
export const EMPLOYEE_TEMPLATE_EXAMPLE: string[] = EMPLOYEE_FLAT_FIELDS.map(
  (f) => TEMPLATE_EXAMPLES[f.name] ?? "",
);

/**
 * Normalize a CSV header or field label for fuzzy auto-matching: lowercase, with
 * every non-alphanumeric run dropped (so "First Name", "first_name" and
 * "firstName" all collapse to "firstname").
 */
export function normalizeHeader(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

/** Select fields keyed by name, carrying their option list + required flag. */
const SELECT_FIELDS: Map<string, { options: readonly Option[]; required: boolean }> = new Map(
  ALL_SECTIONS.flatMap((s) => s.fields)
    .filter((f) => f.kind === "select" && f.options)
    .map((f) => [f.name, { options: f.options ?? [], required: Boolean(f.required) }]),
);

/**
 * Best-effort match of a raw imported select value to one of the field's option
 * values: compares the normalized input against each option's `value` and
 * `label` (so "Mr", "Mr.", "MR" and "mr" all resolve to `mr`). Returns the
 * canonical value, or `null` when nothing matches.
 */
function matchSelectValue(value: string, options: readonly Option[]): string | null {
  if (!normalizeHeader(value)) return "";
  // Retry without parenthetical qualifiers, so "Active (Permanent)" and
  // "Part-Time (32 hrs/wk)" resolve to `active` and `part-time`.
  for (const candidate of [value, value.replace(/\([^)]*\)/g, " ")]) {
    const norm = normalizeHeader(candidate);
    if (!norm) continue;
    for (const o of options) {
      if (normalizeHeader(o.value) === norm || normalizeHeader(o.label) === norm) {
        return o.value;
      }
    }
  }
  return null;
}

/** Date fields keyed by name, carrying their required flag. */
const DATE_FIELDS: Map<string, { required: boolean }> = new Map(
  ALL_SECTIONS.flatMap((s) => s.fields)
    .filter((f) => f.kind === "date")
    .map((f) => [f.name, { required: Boolean(f.required) }]),
);

/** Placeholder text an export uses for "no date" (e.g. an open-ended contract). */
const NO_DATE = new Set([
  "na",
  "none",
  "nil",
  "indefinite",
  "permanent",
  "indefinitepermanent",
  "ongoing",
  "openended",
]);

/**
 * Normalize an imported date to ISO yyyy-mm-dd. Accepts year-first dates with
 * unpadded or over-padded parts ("2021-3-1", "1985-001-15", "2021/03/01") when
 * the month and day are in range. Returns "" for a "no date" placeholder, or
 * `null` when the value can't be read as a date.
 */
function coerceImportDate(value: string): string | null {
  const ymd = value.match(/^(\d{4})[-/.](\d{1,3})[-/.](\d{1,3})$/);
  if (ymd) {
    const month = Number(ymd[2]);
    const day = Number(ymd[3]);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    return `${ymd[1]}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }
  return NO_DATE.has(normalizeHeader(value)) ? "" : null;
}

/**
 * Clean a single imported row before validation/import. Select columns are
 * coerced to their canonical option value with lenient matching; an unrecognized
 * value in an *optional* select is dropped (set to "") rather than failing the
 * whole row, while a required select keeps the raw value so validation surfaces
 * a clear "select a …" error. Date columns are normalized to ISO, and an
 * unreadable date is left raw so validation reports it. Other fields pass
 * through untouched.
 */
export function coerceImportRecord(raw: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = { ...raw };
  for (const [name, { options, required }] of SELECT_FIELDS) {
    const value = raw[name];
    if (value === undefined || value === "") continue;
    const matched = matchSelectValue(value, options);
    if (matched !== null) out[name] = matched;
    else if (!required) out[name] = "";
    // required + unmatched: leave raw so the enum's custom message fires.
  }
  for (const [name, { required }] of DATE_FIELDS) {
    const value = raw[name]?.trim();
    if (!value) continue;
    const date = coerceImportDate(value);
    if (date === null || (date === "" && required)) continue;
    out[name] = date;
  }
  return out;
}
